import { defineComponent, computed, onMounted, onUpdated, inject, ref } from 'vue';

import { PortalType } from '@/enums/portal-types';
import { IQuestion, IQuestionType } from '@/interfaces/questions.interface';
import { Routes } from "@/enums/routes";
import { IStore } from "@/interfaces/state.interface";

import { PersonalityTestTracker } from "@/classes/tracker";
import ImageLoader from '@/classes/image-loader';

export default defineComponent({
	props: {
		question: Number
	},
	setup: (props) => {
		const store = inject('store') as IStore;
		const questionNumber = ref(props.question);
		const questionTotal = 60;
		const questionTypes: IQuestionType[] = [{
			maxValue: 59,
			name: 'activity',
			options: [{
				text: '&#x1F634;',
				value: 1
			},{
				text: '&#x1F615;',
				value: 2
			},{
				text: '&#x1F610;',
				value: 3
			},{
				text: '&#x1F603;',
				value: 4
			},{
				text: '&#x1F60D;',
				value: 5
			}]
		},{
			maxValue: 60,
			name: 'countryOfStudy',
			options: [{
				text: 'Europe',
				value: 401
			},{
				text: 'Northern Europe',
				value: 402
			},{
				text: 'Southern Europe',
				value: 403
			},{
				text: 'United Kingdom',
				value: 404
			},{
				text: 'US & Canada',
				value: 405
			},{
				text: 'Australia',
				value: 406
			}]
		},{
			maxValue: 61,
			name: 'degree',
			options: [{
				text: "Bachelor's degree",
				value: PortalType.BACHELORS
			},{
				text: "Master's degree",
				value: PortalType.MASTERS
			},{
				text: "PhD degree",
				value: PortalType.PHD
			},{
				text: "Online degree",
				value: PortalType.DISTANCE_LEARNING
			},{
				text: "Course certificate",
				value: PortalType.SHORT_COURSES
			}]
		}];
		const questions = computed((): IQuestion[] => {
			return store.getters.questions();
		});
		const currentQuestionType = computed((): IQuestionType | undefined => {
			return questionTypes.find(
				questionType => questionNumber.value !== undefined && questionNumber.value <= questionType.maxValue
			);
		});
		const isPersonalityQuestion = computed((): boolean => {
			const questionType = currentQuestionType.value;
			if (!questionType) {
				return false;
			}

			return questionType.name === 'activity';
		});
		const question = computed((): IQuestion | undefined => {
			return store.getters.question(questionNumber.value as number);
		});
		const progress = computed((): number => {
			const questionIndex = questionNumber.value as number;
			switch (questionIndex) {
				case 0: return 0;
				case 1: return 10;
				case 2: return 14;
				case 3: return 17;
				case 4: return 20;
				case 5: return 23;
				case 6: return 25;
				case 7: return 27;
				case 8: return 29;
				case 9: return 31;
				default: return ((questionIndex - 10) / 50) * 69 + 31;
			}
		});

		const trackQuestionnaireAnswers = (): void => {
			const answers = questions.value.map((specificQuestion) => specificQuestion.answer);
			PersonalityTestTracker.fireTracking('questionnaire_answers', JSON.stringify(answers));
		};

		const trackQuestionnaireMilestones = (): void => {
			const questionIndex = questionNumber.value as number;
			// Only track it when specific question numbers were reached to avoid sending too many events.
			if (![0, 1, 4, 9, 19, 29, 39, 49].includes(questionIndex)) {
				return;
			}

			PersonalityTestTracker.fireTracking('questionnaire_milestone', questionIndex.toString());
		};

		const preloadNextQuestionImage = (): void => {
			const questionIndex = questionNumber.value as number;
			// Make sure, before continuing, that there is a next question to pre-load.
			if (questionIndex > questionTotal) {
				return;
			}

			ImageLoader.preload(questionIndex + 1);
		};

		const routeToNextQuestion = (answer: number | string): void => {
			const questionType = currentQuestionType.value;
			const questionIndex = questionNumber.value as number;
			if (!questionType) {
				return;
			}

			if (questionType.name === 'degree') {

				store.actions.setPreferredPortal(answer as PortalType);
				// Only track the questionnaire answers on the last question.
				trackQuestionnaireAnswers();
				store.mutations.setCurrentRoute(Routes.FORM);
			} else {
				questionNumber.value = questionIndex + 1;
			}
		};

		const answerQuestion = (answer: number | string): void => {
			const id = questionNumber.value as number;
			const answerObject = {
				id,
				answer
			};

			trackQuestionnaireMilestones();

			store.actions.answerQuestion(answerObject);
			routeToNextQuestion(answer);
		};

		const restartTest = (): void => {
			const questionIndex = questionNumber.value as number;
			PersonalityTestTracker.fireTracking('test_restart', questionIndex.toString());

			store.actions.resetStoredAnswers();
			store.mutations.setCurrentRoute(Routes.HOME);
		};

		onMounted(preloadNextQuestionImage);
		onUpdated(preloadNextQuestionImage);

		return {
			isPersonalityQuestion,
			currentQuestionType,
			questionNumber,
			questionTotal,
			question,
			progress,
			answerQuestion,
			restartTest
		};
	}
});
